/**
 * 飞书（lark-cli）用户授权要一次申全的 scope 清单——单一来源（U5，周审 2026-10-15）。
 *
 * 业务背景：员工点「一键授权」或走安装向导后，用到某个飞书技能时又撞「缺权限」被迫补授权。
 * 原因是两条授权入口各用一套 scope 策略、清单又只管写类：设置页一键授权只申请应用已开通的推荐
 * scope（`--recommend`），向导走 `--domain all` + 手写的写类补全集，读妙记要的
 * `minutes:minutes:readonly` 两边都不保证有。
 *
 * 消费方（都从这一份取，不各写一份）：
 * - apps/desktop/src/main/feishu-authorize.ts —— 一键授权：`auth login --recommend --scope <清单>`；
 * - plugins/feishu/skills/lark-shared/SKILL.md 生成段 —— 向导 install_scope_bundle 第 2 条 login，
 *   由 scripts/sync-feishu-required-scopes.mjs 从本 JSON 生成，投影不手改；
 * - apps/desktop/src/main/__tests__/lark-scope-bundle-superset.test.ts —— 「全部 lark-* 技能声明的
 *   scope ⊆ userScopes ∪ appOnlyScopes」机械门。
 *
 * `appOnlyScopes`：技能文档里出现、但只属于应用（bot）身份的 scope——在开放平台后台给应用开，
 * 用户 `auth login` 申请不到，放进用户清单只会让每次授权都报「未被授予」。
 */
import raw from "./feishu-required-scopes.json" with { type: "json" };
const SCOPE_SHAPE = /^[a-z][a-z0-9_]*(?::[a-z0-9_.]+)+$/;
function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
}
function parseSkills(value, where) {
    if (!Array.isArray(value) || value.length === 0 || !value.every(isNonEmptyString)) {
        throw new Error(`${where}.skills must be a non-empty string array`);
    }
    return value;
}
function parseScopeName(value, where) {
    if (!isNonEmptyString(value) || !SCOPE_SHAPE.test(value)) {
        throw new Error(`${where}.scope must look like domain:resource[:action]`);
    }
    return value;
}
function asRecord(value, where) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new Error(`${where} must be an object`);
    }
    return value;
}
function parseUserScope(value, index) {
    const where = `feishu-required-scopes.json userScopes[${index}]`;
    const row = asRecord(value, where);
    const scope = parseScopeName(row.scope, where);
    if (row.access !== "read" && row.access !== "write") {
        throw new Error(`${where}.access must be "read" or "write"`);
    }
    if (row.note !== undefined && typeof row.note !== "string") {
        throw new Error(`${where}.note must be a string`);
    }
    return {
        scope,
        access: row.access,
        skills: parseSkills(row.skills, where),
        ...(row.note !== undefined ? { note: row.note } : {}),
    };
}
function parseAppOnlyScope(value, index) {
    const where = `feishu-required-scopes.json appOnlyScopes[${index}]`;
    const row = asRecord(value, where);
    if (!isNonEmptyString(row.reason))
        throw new Error(`${where}.reason must be a non-empty string`);
    return { scope: parseScopeName(row.scope, where), skills: parseSkills(row.skills, where), reason: row.reason };
}
export function parseFeishuRequiredScopes(input) {
    const root = asRecord(input, "feishu-required-scopes.json");
    if (!Array.isArray(root.userScopes) || root.userScopes.length === 0) {
        throw new Error("feishu-required-scopes.json userScopes must be a non-empty array");
    }
    if (!Array.isArray(root.appOnlyScopes)) {
        throw new Error("feishu-required-scopes.json appOnlyScopes must be an array");
    }
    const userScopes = root.userScopes.map(parseUserScope);
    const appOnlyScopes = root.appOnlyScopes.map(parseAppOnlyScope);
    const seen = new Set();
    for (const { scope } of [...userScopes, ...appOnlyScopes]) {
        if (seen.has(scope))
            throw new Error(`feishu-required-scopes.json lists ${scope} twice`);
        seen.add(scope);
    }
    return { userScopes, appOnlyScopes };
}
const parsed = parseFeishuRequiredScopes(raw);
/** 用户授权要申请的 scope（读 + 写）。 */
export const FEISHU_REQUIRED_USER_SCOPES = parsed.userScopes;
/** 技能里出现、但只属于应用身份的 scope（不进用户授权）。 */
export const FEISHU_APP_ONLY_SCOPES = parsed.appOnlyScopes;
/** `lark-cli auth login --scope` 的参数值：空格分隔、字典序。 */
export const FEISHU_REQUIRED_USER_SCOPE_ARG = FEISHU_REQUIRED_USER_SCOPES
    .map((row) => row.scope)
    .sort()
    .join(" ");
//# sourceMappingURL=feishu-required-scopes.js.map